/**
 * What the case knows: every record on it, a page at a time, filtered by the
 * API — by standing (corroborated first, then claimed, contradicted,
 * discounted and unverifiable, each counted from the record index) and by
 * subject — with its number, source, basis and standing, who decided whether
 * it's about the subject, and where it came from. A record found in the
 * partner's return links to its paragraph when the document shown is that
 * return; a record drawn from one of the case's files opens the file; a page
 * research read opens as it saw it (the capture); otherwise its source.
 *
 * A record opened here ("See it among the case's records") opens its tab and
 * page; one a newer record replaced is shown first, since no list holds it.
 */

import { memo, useEffect, useMemo, useRef, useState } from 'react';

import {
  Anchor,
  Badge,
  Card,
  Group,
  Loader,
  Pagination,
  SegmentedControl,
  Select,
  Spoiler,
  Stack,
  Text,
} from '@mantine/core';
import { IconFile, IconFileText } from '@tabler/icons-react';

import {
  paragraphInDocument,
  type ChecklistItem,
  type EvidenceIndexEntry,
  type EvidenceItem,
  type EvidencePage,
  type EvidenceQuery,
  type EvidenceStatus,
  type WorkbenchFile,
  type WorkbenchHeader,
} from '@/types/workbench';
import { CaptureViewer } from './CaptureViewer';
import { EVIDENCE_STATUS, sourceLabel, words } from './format';
import { IdentityBadge, scopeText } from './RecordView';
import classes from './Workbench.module.css';

/** A tab: one standing, or every record. */
export type EvidenceTab = EvidenceStatus | 'all';

/** Corroborated first; every record last. */
export const EVIDENCE_TABS: readonly EvidenceTab[] = [
  'corroborated',
  'claimed',
  'contradicted',
  'discounted',
  'unverifiable',
  'all',
];

export const EVIDENCE_PAGE_SIZE = 50;

/** Where the list stands: its tab, its subject, its page. */
export interface EvidenceFilters {
  tab: EvidenceTab;
  subjectId: string | null;
  page: number;
}

/** The read the filters make. */
export function evidenceQuery(filters: EvidenceFilters): EvidenceQuery {
  return {
    page: filters.page,
    pageSize: EVIDENCE_PAGE_SIZE,
    status: filters.tab === 'all' ? null : filters.tab,
    subjectId: filters.subjectId,
  };
}

/** Each tab's live records, of one subject where one is chosen. */
export function tabCounts(
  index: EvidenceIndexEntry[],
  subjectId: string | null
): Record<EvidenceTab, number> {
  const counts: Record<EvidenceTab, number> = {
    corroborated: 0,
    claimed: 0,
    contradicted: 0,
    discounted: 0,
    unverifiable: 0,
    all: 0,
  };
  for (const entry of index) {
    if (entry.superseded || (subjectId !== null && entry.subjectId !== subjectId)) {
      continue;
    }
    counts[entry.status] += 1;
    counts.all += 1;
  }
  return counts;
}

/** Where the list opens: the first tab holding a record, every subject. */
export function firstFilters(index: EvidenceIndexEntry[]): EvidenceFilters {
  const counts = tabCounts(index, null);
  return { tab: EVIDENCE_TABS.find((tab) => counts[tab] > 0) ?? 'all', subjectId: null, page: 1 };
}

/** Where a record sits: its standing's tab, every subject, and its page —
 * the read lists records in the order they were made, as their numbers
 * run. None for a record the index doesn't hold or a newer one replaced. */
export function placeOf(index: EvidenceIndexEntry[], id: string): EvidenceFilters | null {
  const entry = index.find((e) => e.id === id);
  if (!entry || entry.superseded) {
    return null;
  }
  const before = index.filter(
    (e) => !e.superseded && e.status === entry.status && e.number < entry.number
  ).length;
  return {
    tab: entry.status,
    subjectId: null,
    page: Math.floor(before / EVIDENCE_PAGE_SIZE) + 1,
  };
}

interface EvidenceViewProps {
  header: WorkbenchHeader;
  /** Every record by number: the tabs' counts. */
  index: EvidenceIndexEntry[] | null;
  /** The page the filters read. */
  evidence: EvidencePage | null;
  filters: EvidenceFilters;
  onFiltersChange: (filters: EvidenceFilters) => void;
  /** The checklist, for the items a record bears on. */
  checklist: ChecklistItem[] | null;
  /** The case's files, for the one a record was drawn from. */
  files?: WorkbenchFile[] | null;
  focusedId: string | null;
  /** The record in focus, read by its id when the page doesn't hold it. */
  opened?: EvidenceItem | null;
  /** The record in focus isn't on this case. */
  openedMissing?: boolean;
  onOpenChecklistItem: (itemId: string) => void;
  onShowParagraph: (paraId: string) => void;
  /** Open the file a record was drawn from, by its key. */
  onOpenFile?: (key: string) => void;
}

/** The file a record was drawn from: the attachment or the report_data row
 * it names, among the case's files that have a key. */
export function fileOfRecord(
  item: Pick<EvidenceItem, 'conversationAttachmentId' | 'reportDataId'>,
  files: WorkbenchFile[] | null | undefined
): WorkbenchFile | null {
  return (
    files?.find(
      (file) =>
        file.key !== null &&
        ((item.conversationAttachmentId !== null &&
          file.conversationAttachmentId === item.conversationAttachmentId) ||
          (item.reportDataId !== null && file.reportDataId === item.reportDataId))
    ) ?? null
  );
}

/** One item. Memoised: a case can hold hundreds, and most don't change when
 * the case does. */
const EvidenceCard = memo(
  ({
    item,
    header,
    checklistTitles,
    file,
    focused,
    onOpenChecklistItem,
    onShowParagraph,
    onOpenFile,
  }: Readonly<{
    item: EvidenceItem;
    header: WorkbenchHeader;
    checklistTitles: Map<string, string>;
    /** The case's file it was drawn from, if one has a key. */
    file: WorkbenchFile | null;
    focused: boolean;
    onOpenChecklistItem: (itemId: string) => void;
    onShowParagraph: (paraId: string) => void;
    onOpenFile?: (key: string) => void;
  }>) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [seeing, setSeeing] = useState(false);
    const status = EVIDENCE_STATUS[item.status];
    const paraId = paragraphInDocument(item, header.document);
    const scope = item.kind === 'search' ? scopeText(item.scope) : null;

    useEffect(() => {
      if (focused) {
        ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      }
    }, [focused]);

    return (
      <Card
        ref={ref}
        withBorder
        padding="sm"
        radius="md"
        className={classes.offscreenLazy}
        data-testid={`evidence-${item.id}`}
        data-focused={focused || undefined}
        style={focused ? { borderColor: 'var(--mantine-color-orange-5)' } : undefined}
      >
        <Stack gap={6}>
          <Group gap={6} wrap="wrap">
            <Text size="xs" fw={600} c="dimmed" data-testid={`evidence-number-${item.id}`}>
              Record {item.number}
            </Text>
            <Badge size="xs" variant="light" color={status.color}>
              {status.label}
            </Badge>
            {item.supersededAt && (
              <Badge size="xs" variant="outline" color="gray">
                Superseded
              </Badge>
            )}
            {item.kind === 'search' && (
              <Badge size="xs" variant="outline" color="gray">
                Search
              </Badge>
            )}
            <Badge size="xs" variant="outline" color="gray" style={{ textTransform: 'none' }}>
              {sourceLabel(item.sourceType)}
            </Badge>
            <Badge size="xs" variant="outline" color="gray" style={{ textTransform: 'none' }}>
              {words(item.basis)}
            </Badge>
            <IdentityBadge basis={item.identityBasis} />
            {item.decidedByType && (
              <Text size="xs" c="dimmed">
                decided by {words(item.decidedByType)}
              </Text>
            )}
          </Group>
          <Text size="sm" style={{ wordBreak: 'break-word' }}>
            {item.statement}
          </Text>
          {item.statusReason && (
            <Text size="xs" c="dimmed">
              {item.statusReason}
            </Text>
          )}
          {scope && (
            <Text size="xs" c="dimmed">
              Searched — {scope}
            </Text>
          )}
          {item.quote && (
            <Spoiler maxHeight={0} showLabel="Quote" hideLabel="Hide quote">
              <Text size="xs" fs="italic" c="dimmed" style={{ whiteSpace: 'pre-line' }}>
                “{item.quote}”
              </Text>
            </Spoiler>
          )}
          <Group gap="md" wrap="wrap">
            {item.links.map((link) => (
              <Anchor
                key={`${link.checklistItemId}-${link.relation}`}
                component="button"
                type="button"
                size="xs"
                onClick={() => onOpenChecklistItem(link.checklistItemId)}
                data-testid={`evidence-checklist-${link.checklistItemId}`}
              >
                {words(link.relation)}{' '}
                {checklistTitles.get(link.checklistItemId) ?? 'a checklist item'}
              </Anchor>
            ))}
            {paraId ? (
              <Anchor
                component="button"
                type="button"
                size="xs"
                onClick={() => onShowParagraph(paraId)}
                data-testid={`evidence-paragraph-${item.id}`}
              >
                <Group gap={4}>
                  <IconFileText size={12} />
                  Show in the document
                </Group>
              </Anchor>
            ) : item.url ? (
              <Anchor href={item.url} target="_blank" rel="noopener noreferrer" size="xs">
                {item.url}
              </Anchor>
            ) : null}
            {file?.key && onOpenFile ? (
              <Anchor
                component="button"
                type="button"
                size="xs"
                onClick={() => onOpenFile(file.key ?? '')}
                data-testid={`evidence-file-${item.id}`}
              >
                <Group gap={4}>
                  <IconFile size={12} />
                  {file.key} · {file.filename}
                </Group>
              </Anchor>
            ) : null}
            {item.hasCapture ? (
              <Anchor
                component="button"
                type="button"
                size="xs"
                onClick={() => setSeeing(true)}
                data-testid={`evidence-capture-${item.id}`}
              >
                As research saw it
              </Anchor>
            ) : null}
          </Group>
        </Stack>
        {item.hasCapture ? (
          <CaptureViewer
            reportId={header.reportId}
            evidenceId={item.id}
            opened={seeing}
            onClose={() => setSeeing(false)}
          />
        ) : null}
      </Card>
    );
  }
);

export function EvidenceView({
  header,
  index,
  evidence,
  filters,
  onFiltersChange,
  checklist,
  files = null,
  focusedId,
  opened = null,
  openedMissing = false,
  onOpenChecklistItem,
  onShowParagraph,
  onOpenFile,
}: Readonly<EvidenceViewProps>) {
  const checklistTitles = useMemo(
    () => new Map((checklist ?? []).map((c) => [c.id, c.title])),
    [checklist]
  );
  const subjectNames = useMemo(
    () => new Map(header.subjects.map((s) => [s.id, s.name])),
    [header.subjects]
  );
  const counts = useMemo(
    () => (index ? tabCounts(index, filters.subjectId) : null),
    [index, filters.subjectId]
  );

  if (evidence === null || counts === null) {
    return <Loader size="sm" />;
  }

  const items = evidence.items;
  const pages = Math.max(1, Math.ceil(evidence.total / evidence.pageSize));
  const bySubject = new Map<string, EvidenceItem[]>();
  for (const item of items) {
    const key = item.subjectId ?? 'case';
    bySubject.set(key, [...(bySubject.get(key) ?? []), item]);
  }
  const tabLabel = (tab: EvidenceTab) =>
    `${tab === 'all' ? 'All' : EVIDENCE_STATUS[tab].label} ${counts[tab].toLocaleString()}`;

  return (
    <Stack gap="md" data-testid="evidence-view">
      <Group justify="space-between" wrap="wrap" gap="xs">
        <SegmentedControl
          size="xs"
          value={filters.tab}
          onChange={(v) => onFiltersChange({ ...filters, tab: v as EvidenceTab, page: 1 })}
          data={EVIDENCE_TABS.map((tab) => ({ value: tab, label: tabLabel(tab) }))}
          data-testid="evidence-filter"
        />
        {header.subjects.length > 1 && (
          <Select
            size="xs"
            w={240}
            aria-label="Subject"
            value={filters.subjectId ?? ''}
            onChange={(v) => onFiltersChange({ ...filters, subjectId: v || null, page: 1 })}
            data={[
              { value: '', label: 'Every subject' },
              ...header.subjects.map((s) => ({ value: s.id, label: s.name })),
            ]}
            allowDeselect={false}
            data-testid="evidence-subject"
          />
        )}
      </Group>

      {opened && (
        <Stack gap="xs" data-testid="evidence-opened">
          <Text fw={600}>
            The record you opened ·{' '}
            {opened.subjectId === null
              ? 'the case'
              : (subjectNames.get(opened.subjectId) ?? 'a subject')}
          </Text>
          <Text size="xs" c="dimmed">
            {opened.supersededAt
              ? 'A newer record replaced it, so no list holds it.'
              : "It isn't on this page."}
          </Text>
          <EvidenceCard
            item={opened}
            header={header}
            checklistTitles={checklistTitles}
            file={fileOfRecord(opened, files)}
            focused
            onOpenChecklistItem={onOpenChecklistItem}
            onShowParagraph={onShowParagraph}
            onOpenFile={onOpenFile}
          />
        </Stack>
      )}
      {openedMissing && (
        <Text size="sm" c="dimmed" data-testid="evidence-opened-missing">
          The record you opened isn&apos;t on this case.
        </Text>
      )}

      {counts.all === 0 && (
        <Text size="sm" c="dimmed">
          No evidence on this case yet — it arrives as agents read sources.
        </Text>
      )}
      {counts.all > 0 && evidence.total === 0 && (
        <Text size="sm" c="dimmed">
          No records here.
        </Text>
      )}
      {evidence.total > 0 && (
        <Text size="xs" c="dimmed" data-testid="evidence-count">
          {evidence.total.toLocaleString()} record{evidence.total === 1 ? '' : 's'}
          {pages > 1 ? ` · page ${evidence.page} of ${pages}` : ''}
        </Text>
      )}

      {[...bySubject].map(([key, group]) => (
        <Stack key={key} gap="xs">
          <Text fw={600}>
            {key === 'case' ? 'The case' : (subjectNames.get(key) ?? 'A subject')}
          </Text>
          {group.map((item) => (
            <EvidenceCard
              key={item.id}
              item={item}
              header={header}
              checklistTitles={checklistTitles}
              file={fileOfRecord(item, files)}
              focused={item.id === focusedId}
              onOpenChecklistItem={onOpenChecklistItem}
              onShowParagraph={onShowParagraph}
              onOpenFile={onOpenFile}
            />
          ))}
        </Stack>
      ))}
      {pages > 1 && (
        <Pagination
          size="sm"
          total={pages}
          value={evidence.page}
          onChange={(page) => onFiltersChange({ ...filters, page })}
          data-testid="evidence-pages"
        />
      )}
    </Stack>
  );
}
